import * as React from 'react';
import { t, tryT } from '@reos/i18n';
import { canSeeMenuItem } from '../lib/capabilities.js';
import { useSession } from '../stores/session.js';
import { DomainApiError } from '../lib/api-error.js';
import {
  approveCorrespondence,
  listPendingCorrespondence,
  rejectCorrespondence,
  type PendingCorrespondenceItem,
} from '../lib/correspondence-api.js';
import type { QueueSectionProps } from '../lib/approval-queue-section.js';

/**
 * Krok A4 bod 2 — FRONTA „DOTAZY KE SCHVALENI" (sekce na stavajici strance
 * Schvaleni, stejne jako fronta reportu z A5: zadna nova polozka v menu).
 *
 * Co clovek u kazdeho navrhu vidi: komu se pise, cely text dopisu (odchazi
 * jmenem kancelare, tak ho musi byt mozne precist celý), kolik otazek nese,
 * jestli ramec formuloval lokalni model, a do kdy zadame o odpoved. Dve akce:
 * schvalit odeslani (u SVJ/obce s DOPLNENOU adresou), nebo zamitnout s duvodem.
 *
 * Zadny text natvrdo (CLAUDE.md pravidlo 5) - vsechno pres `t()`; strojove kody
 * z API se prekladaji tim samym katalogem, ktery pouziva domena.
 *
 * `window.prompt` je minimalni UI (tentyz vzor jako A5 a C3): povinnou adresu
 * i povinny duvod vynucuje BACKEND, tohle je jen cesta, jak je zadat.
 */
export function CorrespondenceQueue({ inQueue = false, onCount }: QueueSectionProps = {}): React.JSX.Element | null {
  // POZOR (nalez N1 z A5): selektor musi vracet STABILNI referenci. `s.user?.roles ?? []`
  // by pri kazdem renderu vratil nove pole a `useSyncExternalStore` by se zacyklil.
  const user = useSession((s) => s.user);
  const allowed = canSeeMenuItem(user?.roles ?? [], 'dd.correspondence.approve');
  const [items, setItems] = React.useState<readonly PendingCorrespondenceItem[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [busyId, setBusyId] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    try {
      setItems(await listPendingCorrespondence());
    } catch (e) {
      setError(
        e instanceof DomainApiError ? e.message : t('dd.correspondence.loadError', { locale: 'cs' }),
      );
    }
  }, []);

  React.useEffect(() => {
    if (allowed) void load();
  }, [allowed, load]);

  /*
   * Pocet hlasi stranka vys, az kdyz je znamy — `null` je „jeste nevim".
   * Kdo frontu nesmi videt, ma cekat na rozhodnuti NULA veci; bez teto vetve
   * by stranka na prazdny stav cekala navzdy (fronta by nikdy neodpovedela).
   */
  React.useEffect(() => {
    if (!allowed) onCount?.(0);
    else if (items !== null) onCount?.(items.length);
  }, [allowed, items, onCount]);

  /** Kdo nesmi rozhodovat o odeslani, nevidi ani frontu (vzor A5). */
  if (!allowed) return null;

  async function approve(item: PendingCorrespondenceItem): Promise<void> {
    /**
     * Adresu se doptavame JEN u navrhu, ktery ji nema. Kdyz ji navrh ma
     * (overeny ciselnik spravcu siti), prepsat ji nejde - backend to odmitne.
     */
    let address: string | undefined;
    if (item.toAddr === null) {
      const answer = window.prompt(t('dd.correspondence.addressLabel', { locale: 'cs' }));
      if (answer === null || answer.trim() === '') return;
      address = answer.trim();
    }
    const note = window.prompt(t('dd.correspondence.noteLabel', { locale: 'cs' }));
    if (note === null) return;
    setBusyId(item.id);
    setError(null);
    setNotice(null);
    try {
      await approveCorrespondence(item.id, {
        ...(address === undefined ? {} : { recipientAddress: address }),
        note,
      });
      setNotice(t('dd.correspondence.approved', { locale: 'cs' }));
      await load();
    } catch (e) {
      setError(
        e instanceof DomainApiError ? e.message : t('dd.correspondence.loadError', { locale: 'cs' }),
      );
    } finally {
      setBusyId(null);
    }
  }

  async function reject(item: PendingCorrespondenceItem): Promise<void> {
    const reason = window.prompt(t('dd.correspondence.rejectReason', { locale: 'cs' }));
    // Zruseny dialog i prazdny duvod = zadna akce; duvod je povinny i v API.
    if (reason === null || reason.trim().length < 3) return;
    setBusyId(item.id);
    setError(null);
    setNotice(null);
    try {
      await rejectCorrespondence(item.id, reason.trim());
      setNotice(t('dd.correspondence.rejected', { locale: 'cs' }));
      await load();
    } catch (e) {
      setError(
        e instanceof DomainApiError ? e.message : t('dd.correspondence.loadError', { locale: 'cs' }),
      );
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="mb-8" data-testid="correspondence-queue">
      {/* Krok UXF4 bod 8 (#29): viz `report-release-queue.tsx` — oddil, ne stranka.
          Krok UXF5 bod 4: v jedne fronte „Čeká na rozhodnutí (N)" nadpis nestoji. */}
      {inQueue ? null : (
        <h2 className="mb-1 text-lg font-semibold">{t('dd.correspondence.title', { locale: 'cs' })}</h2>
      )}

      {error !== null && (
        <p role="alert" className="mb-2 text-red-600">
          {error}
        </p>
      )}
      {notice !== null && (
        <p className="mb-2 text-sm text-green-700" data-testid="correspondence-notice">
          {notice}
        </p>
      )}

      {items === null ? null : items.length === 0 ? (
        inQueue ? null : (
          <p data-testid="correspondence-empty">{t('dd.correspondence.empty', { locale: 'cs' })}</p>
        )
      ) : (
        <ul className="space-y-4" data-testid="correspondence-list">
          {items.map((item) => (
            <li key={item.id} className="border-t pt-2" data-testid={`correspondence-row-${item.id}`}>
              <div className="text-sm font-semibold">
                {recipientLabel(item.recipientKind)}
                {item.recipientName !== null ? ` — ${item.recipientName}` : ''}
                <span className="ml-2 font-normal text-gray-600">{kindLabel(item.messageKind)}</span>
              </div>
              <div className="text-sm">{item.subject}</div>
              <div className="text-sm text-gray-600">
                {t('dd.correspondence.questionCount', {
                  locale: 'cs',
                  values: { count: item.questionKeys.length },
                })}
              </div>
              {item.toAddr === null && (
                <p className="text-sm text-amber-700" data-testid={`correspondence-address-missing-${item.id}`}>
                  {t('dd.correspondence.addressMissing', { locale: 'cs' })}
                </p>
              )}
              {item.modelGenerated && (
                <p className="text-xs text-gray-500">
                  {t('dd.correspondence.modelGenerated', { locale: 'cs' })}
                </p>
              )}
              {/* Cely text dopisu: odchazi jmenem kancelare, takze si ho clovek musi precist. */}
              <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap bg-gray-50 p-2 text-xs">
                {item.bodyText}
              </pre>
              <div className="mt-2">
                <button
                  type="button"
                  className="rh-btn rh-btn--primary mr-2"
                  disabled={busyId === item.id}
                  onClick={() => void approve(item)}
                  data-testid={`correspondence-approve-${item.id}`}
                >
                  {t('dd.correspondence.approve', { locale: 'cs' })}
                </button>
                <button
                  type="button"
                  className="rh-btn"
                  disabled={busyId === item.id}
                  onClick={() => void reject(item)}
                  data-testid={`correspondence-reject-${item.id}`}
                >
                  {t('dd.correspondence.reject', { locale: 'cs' })}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Strojovy kod -> veta. Neznamy kod se vypise, jak prisel: novejsi verze API
 * nesmi shodit obrazovku (poucení N6 z H1, vzor z A5).
 */
function recipientLabel(kind: string): string {
  const key = `dd.correspondence.recipient.${kind}` as 'dd.correspondence.recipient.HOA';
  return tryT(key, { locale: 'cs' }) ?? kind;
}

function kindLabel(messageKind: string): string {
  const key = `dd.correspondence.kind.${messageKind}` as 'dd.correspondence.kind.REQUEST';
  return tryT(key, { locale: 'cs' }) ?? messageKind;
}
